import dynamic from 'next/dynamic'
import { createContext, useState, useEffect, useMemo } from 'react'
import { useRouter } from 'next/router'
import { useSelector, useDispatch } from 'react-redux'
import { mutate } from 'swr'
import { useCookies } from 'react-cookie'

import RequestCore from 'utils/request'
import {
  selectWebview,
  setWebviewAuthFinished,
} from 'store/webview/webviewSlice'
import { uuidv4 } from 'utils/helpers'
import { decryptSelfConstructToken, logout } from 'utils/api'
import { setSelfConstructActive } from 'store/selfConstruct/selfConstructSlice'
import { clearForm } from 'store/bookForm/bookFormSlice'

const DynamicWebviewAuth = dynamic(() => import('./WebviewAuth'), {
  ssr: false,
})

export const SET_COOKIE_TIME_KEY = 'set-cookie-time'
export const SET_ROUTE_KEY = 'set-route'

/*
 * Usage: const { token, setToken } = useContext(AuthContext)
 */
export const AuthContext = createContext({
  token: '',
  setToken: (token) => {},
  appId: '',
  webviewAuthFinished: false,
})

export const AuthProvider = ({ children, defaultToken }) => {
  const [initialized, setInitialized] = useState(false)
  const [cookies, setCookie, removeCookie] = useCookies(['token'])
  const [token, setToken] = useState(cookies.token || defaultToken || '')
  const [appId, setAppId] = useState(cookies.appId)
  const webview = useSelector(selectWebview)
  const router = useRouter()

  const logoutClient = async () => {
    setToken('')
    await router.push('/')
    dispatch(setSelfConstructActive(false))
    dispatch(clearForm())
    localStorage.removeItem('refreshToken')
    localStorage.removeItem('isSelfConstruct')

    // remove old service data
    localStorage.removeItem('domesticServiceAddress')
    sessionStorage.removeItem('syDomesticService')
    localStorage.removeItem('isLogin')
  }

  const isCheckTokenRoute = [
    '/profile',
    '/complete-profile',
    '/credit-card',
    '/reservation',
    '/coupon',
    '/recommend-discount',
    '/setting',
    '/reservation/[reservationId]/[orderStatus]',
  ]

  const selfConstructRoute = ['/self-construct-singlereservation']

  // Self Construct login only in specify page
  useEffect(() => {
    const loadDecryptScToken = async () => {
      const { scCode } = router.query
      const logoutExistClient = async () => {
        if (!localStorage.getItem('refreshToken')) return
        const [result, error] = await logout()
        if (!error) {
          localStorage.removeItem('isLogin')
          localStorage.removeItem('refreshToken')
        }
      }

      if (scCode) {
        await logoutExistClient()
        const [data, error] = await decryptSelfConstructToken(scCode)
        if (error) {
          console.error(error.message)
        } else {
          const { text: scToken } = data
          handleSetToken(scToken)
          localStorage.setItem('isLogin', true)
          localStorage.setItem('isSelfConstruct', true)
        }
      }
    }

    if (router.isReady && selfConstructRoute.includes(router.pathname)) {
      loadDecryptScToken()
    }
  }, [router.isReady, router.pathname])

  useEffect(() => {
    if (router.isReady && !selfConstructRoute.includes(router.pathname)) {
      const isSelfConstruct = localStorage.getItem('isSelfConstruct')
      if (isSelfConstruct) logoutClient()
    }
  }, [router.isReady, router.pathname])

  useEffect(() => {
    if (!webview.authFinished) {
      return
    }
    // update SWR cache and cookie using updated token
    if (!token) {
      mutate('/users/me', null, false)
      removeCookie('token', { path: '/' })
      if (
        isCheckTokenRoute.includes(router.pathname) &&
        !localStorage.getItem('isLogin')
      ) {
        localStorage.setItem(SET_ROUTE_KEY, router.asPath)
        router.push('/login')
        return
      }
    } else {
      if (initialized) {
        mutate('/users/me')
        setCookieToken(token)
      }
    }
    setInitialized(true)
  }, [token, webview?.authFinished, router.pathname])

  const dispatch = useDispatch()
  const [webviewAuth, setWebviewAuth] = useState(false)
  useEffect(() => {
    if (webview?.initialized && !webview?.authFinished && router.isReady) {
      if (!!webview?.name) {
        setWebviewAuth(true)
      } else {
        RequestCore.setToken(token)
        dispatch(setWebviewAuthFinished(true))
      }
    }
  }, [webview?.initialized, router.isReady])

  useEffect(() => {
    // init appId if not exist
    if (webview?.initialized && webview?.name !== 'lsApp' && !appId) {
      const newAppId = uuidv4()
      setAppId(newAppId)
      setCookie('appId', newAppId, { path: '/', maxAge: 630720000 })
    }
  }, [webview?.initialized])

  const setCookieToken = (token) => {
    setCookie('token', token, { path: '/', maxAge: 630720000 })
    localStorage.setItem(SET_COOKIE_TIME_KEY, Date.now())
  }

  const handleSetToken = (token, refreshToken, resetAppId = true) => {
    // reset appId if logged out
    if (!token && resetAppId) {
      const newAppId = uuidv4()
      setAppId(uuidv4())
      setCookie('appId', newAppId, { path: '/', maxAge: 630720000 })
    }
    if (token && refreshToken) {
      localStorage.setItem('refreshToken', refreshToken)
    }
    RequestCore.setToken(token)
    setCookieToken(token)

    /**
     * Note: setToken 必須要在 RequestCore.setToken 之後，
     * 否則用 token 觸發事件後，但是取 RequestCore.getToken 有可能會取到舊的
     */
    setToken(token)
  }

  const value = useMemo(
    () => ({
      token: webview?.authFinished ? token : '',
      setToken: handleSetToken,
      appId,
      webviewAuthFinished: webview?.authFinished,
    }),
    [token, appId, webview.authFinished]
  )
  return (
    <AuthContext.Provider value={value}>
      {children}
      {!!webviewAuth && (
        <DynamicWebviewAuth
          token={token}
          setToken={handleSetToken}
          appId={appId}
          setAppId={setAppId}
          setWebviewAuth={setWebviewAuth}
        />
      )}
    </AuthContext.Provider>
  )
}

/*
 * Used in getServerSideProps
 * Usage: const { token, isAuth } = initServerSideAuth(req)
 */
//
export const initServerSideAuth = (req) => {
  const token = req.headers.cookie?.token || ''
  RequestCore.setToken(token)
  return {
    token,
    isAuth: !!token,
  }
}
